import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { logger } from '@infra/telemetry/Logger';

/** Contrôle au plus toutes les 5 minutes, et au retour sur l'onglet. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;
const BANNER_ID = 'nexxus-cloud-update-banner';

/**
 * Scripts d'entrée (modules) d'un document HTML : leurs noms contiennent l'empreinte du build
 * (`/assets/index-AbC123.js`). Deux builds différents n'ont jamais les mêmes noms.
 */
export function extractEntryScripts(html: string): string[] {
  const found = new Set<string>();
  const tag = /<script\b[^>]*>/gi;
  for (const match of html.matchAll(tag)) {
    const element = match[0];
    if (!/type\s*=\s*["']module["']/i.test(element)) continue;
    const src = /src\s*=\s*["']([^"']+)["']/i.exec(element)?.[1];
    if (src) found.add(src);
  }
  return [...found].sort();
}

/** Vrai si le site publié ne porte plus les mêmes scripts que la page ouverte (nouvelle version déployée). */
export function isNewWebVersion(loaded: readonly string[], published: readonly string[]): boolean {
  if (loaded.length === 0 || published.length === 0) return false;
  return loaded.join('|') !== published.join('|');
}

export interface CloudWebVersionWatcherDeps {
  fetchHtml?: () => Promise<string | null>;
  loadedScripts?: () => string[];
  onNewVersion?: () => void;
  setInterval?: (fn: () => void, ms: number) => number;
  now?: () => number;
}

async function defaultFetchHtml(): Promise<string | null> {
  const response = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) return null;
  return response.text();
}

function defaultLoadedScripts(): string[] {
  return extractEntryScripts(document.head.innerHTML + document.body.innerHTML).filter((src) =>
    src.includes('/assets/'),
  );
}

/**
 * Version Cloud (navigateur) : prévient quand une nouvelle version du site a été déployée pendant que
 * l'onglet est resté ouvert. Aucun rechargement automatique (on ne coupe jamais une saisie en cours).
 * Ne passe pas par un service worker (désactivé volontairement côté serveur).
 */
export class CloudWebVersionWatcher {
  private timer: number | null = null;
  private lastCheck = 0;
  private notified = false;
  private readonly loaded: string[];

  constructor(private readonly deps: CloudWebVersionWatcherDeps = {}) {
    this.loaded = (deps.loadedScripts ?? defaultLoadedScripts)();
  }

  start(): void {
    const setTimer = this.deps.setInterval ?? ((fn, ms) => window.setInterval(fn, ms));
    this.timer = setTimer(() => void this.check(), CHECK_INTERVAL_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.check();
      });
    }
  }

  stop(): void {
    if (this.timer != null) window.clearInterval(this.timer);
    this.timer = null;
  }

  async check(): Promise<boolean> {
    if (this.notified) return true;
    const now = (this.deps.now ?? Date.now)();
    if (now - this.lastCheck < MIN_GAP_MS) return false;
    this.lastCheck = now;
    try {
      const html = await (this.deps.fetchHtml ?? defaultFetchHtml)();
      if (!html) return false;
      if (
        !isNewWebVersion(
          this.loaded,
          extractEntryScripts(html).filter((s) => s.includes('/assets/')),
        )
      ) {
        return false;
      }
      this.notified = true;
      (this.deps.onNewVersion ?? showCloudUpdateBanner)();
      return true;
    } catch (err) {
      logger.warn({
        event: 'cloud_version_check_failed',
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }
}

/** Bandeau discret en haut de la page : l'utilisateur recharge quand il le décide. */
export function showCloudUpdateBanner(): void {
  if (document.getElementById(BANNER_ID)) return;
  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.setAttribute('role', 'status');
  banner.style.cssText =
    'position:sticky;top:0;z-index:9999;margin:0;padding:10px 16px;background:#0a121e;color:#fff;font-size:13px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;border-bottom:2px solid #008080;';
  const text = document.createElement('div');
  text.style.flex = '1';
  text.style.minWidth = '200px';
  text.textContent =
    'Une nouvelle version de Nexxus ERP est disponible. Terminez votre saisie, puis rechargez la page pour l’utiliser.';
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Recharger maintenant';
  button.style.cssText =
    'background:#008080;color:#fff;border:none;border-radius:6px;padding:8px 14px;cursor:pointer;font-weight:600;';
  button.addEventListener('click', () => window.location.reload());
  banner.append(text, button);
  document.body.prepend(banner);
}

/** Démarre la surveillance seulement sur le site Cloud publié. */
export function startCloudWebVersionWatcher(): CloudWebVersionWatcher | null {
  if (!isBrowserCloudHost()) return null;
  const watcher = new CloudWebVersionWatcher();
  watcher.start();
  return watcher;
}
